import React, { useState, useEffect } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import { DRAFT_SCOPES } from '../../utils/draftKeys';
import ProeprtyCreate from '../../User/Pages/Components/froms/ProeprtyCreate';
import ProjectCreate from '../../User/Pages/Components/froms/ProjectCreate';
import './AdminPages.css';
import AppIcon from '../../Components/AppIcon';

// ─── Listing Type Modal (same design as CreateListingWrapper) ─────────────────
const ListingTypeModal = ({ onSelect, onCancel }) => (
  <div style={{ position:'fixed', inset:0, zIndex:9999, background:'rgba(15,23,42,0.65)', backdropFilter:'blur(4px)', display:'flex', alignItems:'center', justifyContent:'center' }}>
    <div style={{ background:'#fff', borderRadius:'1.25rem', padding:'2.5rem 2rem', width:'100%', maxWidth:480, boxShadow:'0 25px 60px rgba(0,0,0,0.25)', animation:'fadeSlideUp 0.22s ease' }}>
      <div style={{ textAlign:'center', marginBottom:'2rem' }}>
        <div style={{ width:56, height:56, background:'#eff6ff', borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 1rem' }}>
          <span style={{ fontSize:26, display:'inline-flex' }}><AppIcon name="home" size={26} /></span>
        </div>
        <h2 style={{ fontSize:'1.35rem', fontWeight:800, color:'#0f172a', margin:0 }}>Select Listing Type</h2>
        <p style={{ color:'#64748b', fontSize:'0.9rem', marginTop:'0.4rem' }}>This is required before creating a property</p>
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'1rem' }}>
        {[
          ['Sale','tag','For Sale','Property listed for purchase','#3b82f6','#eff6ff'],
          ['Rent','key','For Rent','Property listed for rental','#8b5cf6','#f5f3ff'],
        ].map(([val,icon,label,desc,color,bg]) => (
          <button key={val} onClick={() => onSelect(val)}
            style={{ padding:'1.5rem 1rem', border:'2px solid #e2e8f0', borderRadius:'0.875rem', background:'#f8fafc', cursor:'pointer', display:'flex', flexDirection:'column', alignItems:'center', gap:'0.6rem', transition:'all 0.18s' }}
            onMouseEnter={e => { e.currentTarget.style.borderColor=color; e.currentTarget.style.background=bg; e.currentTarget.style.transform='translateY(-2px)'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor='#e2e8f0'; e.currentTarget.style.background='#f8fafc'; e.currentTarget.style.transform='none'; }}
          >
            <span style={{ fontSize:36, display:'inline-flex' }}><AppIcon name={icon} size={36} /></span>
            <span style={{ fontSize:'1.05rem', fontWeight:700, color:'#1e293b' }}>{label}</span>
            <span style={{ fontSize:'0.78rem', color:'#64748b' }}>{desc}</span>
          </button>
        ))}
      </div>
      <div style={{ textAlign:'center', marginTop:'1.5rem' }}>
        <button onClick={onCancel} style={{ background:'none', border:'none', color:'#94a3b8', fontSize:'0.875rem', cursor:'pointer', textDecoration:'underline' }}>Cancel — go back</button>
      </div>
    </div>
    <style>{`@keyframes fadeSlideUp { from{opacity:0;transform:translateY(20px)} to{opacity:1;transform:translateY(0)} }`}</style>
  </div>
);

/**
 * OwnPropertyWrapper
 * Auto-sets isOwnProperty=true on the form so listings are tagged with
 * is_own_property=true. Shows a Listing Type modal before Property creation.
 */
const OwnPropertyWrapper = ({ forcedType = null }) => {
  const { pathname } = useLocation();
  const draftScope = pathname.startsWith('/partner') ? DRAFT_SCOPES.PARTNER : DRAFT_SCOPES.ADMIN;
  const [searchParams] = useSearchParams();
  const editId    = searchParams.get('editId');
  const typeParam = searchParams.get('type') || forcedType;

  // Step 1: property or project?
  const [listingType,          setListingType]          = useState(null);
  // Step 2 (property only): Rent or Sale modal
  const [showListingTypeModal, setShowListingTypeModal] = useState(false);
  const [selectedListingType,  setSelectedListingType]  = useState(null);

  useEffect(() => {
    if (typeParam === 'project') {
      setListingType('project');
    }
    // If forcedType is 'property' and not editing, show modal immediately
    if (typeParam === 'property') {
      if (!editId) {
        setShowListingTypeModal(true);
      } else {
        setListingType('property');
      }
    }
  }, []); // eslint-disable-line

  const handleOwnPropertyClick = () => {
    if (editId) {
      setListingType('property');
    } else {
      setShowListingTypeModal(true);
    }
  };

  const handleListingTypeSelect = (type) => {
    setSelectedListingType(type);
    setShowListingTypeModal(false);
    setListingType('property');
  };

  const handleListingTypeCancel = () => {
    setShowListingTypeModal(false);
  };

  // ── Modal overlay ──
  if (showListingTypeModal) {
    return (
      <div className="admin-page">
        <div className="page-header"><h1>Add Own Property</h1></div>
        <ListingTypeModal onSelect={handleListingTypeSelect} onCancel={handleListingTypeCancel} />
      </div>
    );
  }

  // ── Type selection screen (when no forcedType given) ──
  if (!listingType) {
    return (
      <div className="admin-page">
        <div className="page-header">
          <h1>Add Own Property / Project</h1>
          <p>Select what type of own listing you want to add</p>
        </div>
        <div className="admin-card">
          <div className="filter-box-grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
            <button className="btn-primary" style={{ padding: '2rem', fontSize: '1.25rem' }} onClick={handleOwnPropertyClick}>
              <AppIcon name="home" size={20} /> Own Property
            </button>
            <button className="btn-secondary" style={{ padding: '2rem', fontSize: '1.25rem', justifyContent: 'center' }} onClick={() => setListingType('project')}>
              <AppIcon name="project" size={20} /> Own Project
            </button>
          </div>
          <div style={{ marginTop: '1rem', padding: '1rem', background: '#f0f9ff', borderRadius: '0.5rem', fontSize: '0.875rem', color: '#0369a1' }}>
            <strong>Note:</strong> Listings added here will appear in your <em>Own Properties / Own Projects</em> section and will also be visible to Admin.
          </div>
        </div>
      </div>
    );
  }

  // ── Form ──
  return (
    <div className="admin-page">
      <div className="page-header" style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h1>Add Own {listingType === 'property' ? 'Property' : 'Project'}</h1>
          {listingType === 'property' && selectedListingType && (
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: '0.35rem', marginTop: '0.35rem',
              background: selectedListingType === 'Sale' ? '#eff6ff' : '#f5f3ff',
              color: selectedListingType === 'Sale' ? '#1d4ed8' : '#7c3aed',
              border: `1.5px solid ${selectedListingType === 'Sale' ? '#bfdbfe' : '#ddd6fe'}`,
              borderRadius: '1rem', padding: '0.2rem 0.75rem', fontSize: '0.8rem', fontWeight: 700,
            }}>
              <AppIcon name={selectedListingType === 'Sale' ? 'tag' : 'key'} size={16} /> For {selectedListingType}
            </span>
          )}
          <p style={{ fontSize: '0.875rem', color: 'var(--admin-text-muted)', marginTop: '0.25rem' }}>
            This will be saved as your own listing and appear in Own Properties.
          </p>
        </div>
        <button className="btn-secondary" onClick={() => { setListingType(null); setSelectedListingType(null); }}>← Back</button>
      </div>
      <div className="admin-card user-form-wrapper">
        {listingType === 'property'
          ? <ProeprtyCreate isOwnProperty={true} listingTypeProp={selectedListingType} draftScope={draftScope} enableOwnerNamePanel />
          : <ProjectCreate isOwnProperty={true} draftScope={draftScope} enableOwnerNamePanel />
        }
      </div>
    </div>
  );
};

export default OwnPropertyWrapper;
